/** The privacy notice body (no page heading — the caller supplies that). Used
 *  by the public /privacy page and the Profile > Privacy tab so the copy lives
 *  in exactly one place. */
export function PrivacyContent(): JSX.Element {
  return (
    <>
      <p className="text-muted-foreground mb-4">
        Summer Internship stores the following data about each user:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-6">
        <li>Your email address and the name you provide</li>
        <li>Your password, stored only as a salted PBKDF2 hash — never in plaintext</li>
        <li>Your onboarding answers</li>
        <li>Progress through each course (chapters and lessons completed)</li>
        <li>Code you write in the embedded editor (autosaved)</li>
        <li>Quiz attempts and scores</li>
        <li>Activity events (when you open or complete lessons) and a rough online/offline indicator</li>
        <li>
          In-course interaction events — cell views, video playback, exercise runs, and quiz attempts —
          recorded for learning analytics and support. These are retained for approximately 12 months and
          permanently removed when your account is deleted.
        </li>
        <li>Which course videos you start, how far you watch, and whether you finish them.</li>
        <li>Your messages in AI practice exercises (sent to our AI provider to generate tutor responses).</li>
      </ul>
      <p className="text-muted-foreground">
        We do not use third-party analytics, advertising, or behavioral tracking.
      </p>

      <section className="mt-8">
        <h2 className="text-lg font-semibold mb-2">How sign-in works</h2>
        <p className="text-sm text-muted-foreground">
          Authentication is handled in-house — there is no third-party identity provider. You sign in
          with an email and password; a successful sign-in issues a session token stored in our
          database and delivered as a secure, HttpOnly cookie. We do not use this cookie for tracking.
        </p>
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-semibold mb-2">Sub-processors</h2>
        <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
          <li>
            <a className="text-primary underline" href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer">
              Cloudflare
            </a>{" "}
            — hosts the application and stores your data (database, key-value storage, and encrypted backups).
          </li>
          <li>
            <strong>Awning &amp; Pixel and Resend</strong> — send the invitation and password-reset
            emails. Awning &amp; Pixel&apos;s email service passes each one to Resend to deliver. They see
            your email address and the email we send you, with your sign-up or reset link in it.
            Awning &amp; Pixel keeps a record of each email sent, with the address and subject line. Your
            course work and progress are not shared with them.
          </li>
          {/* YouTube / Vimeo — add these when any course uses external video embeds */}
          <li>
            <strong>YouTube (Google LLC)</strong> — When a lesson embeds a YouTube video and you
            choose to play it, your browser connects to YouTube. We use the privacy-enhanced
            <code>youtube-nocookie.com</code> domain and do not load the player until you click
            play. See{" "}
            <a className="text-primary underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
              Google Privacy Policy
            </a>.
          </li>
          <li>
            <strong>Vimeo, Inc.</strong> — Same click-to-load privacy default. See{" "}
            <a className="text-primary underline" href="https://vimeo.com/privacy" target="_blank" rel="noreferrer">
              Vimeo Privacy Policy
            </a>.
          </li>
          {/* Anthropic — shown unconditionally v1; condition on a course's interactiveRuntimes including "prompt-playground" later */}
          <li>
            <strong>Anthropic, PBC</strong> — When a lesson includes an AI practice exercise and you send a message,
            your message (and the exercise's instructions) are sent to Anthropic's Claude API to generate a response.
            See the{" "}
            <a className="text-primary underline" href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noreferrer">
              Anthropic Privacy Policy
            </a>.
          </li>
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-semibold mb-2">Data deletion</h2>
        <p className="text-sm text-muted-foreground">
          To remove your account, contact an admin. They delete your account, which permanently removes
          your progress, saved work, activity events, presence records, coach notes, onboarding answers,
          and all sessions. This action is irreversible.
        </p>
      </section>
    </>
  );
}
